import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { User } from '@app/models/user.model';
import { EMPTY, Observable, Subject, map, startWith, takeUntil } from 'rxjs';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlattener } from '@angular/material/tree';
import { Select, Store } from '@ngxs/store';
import { DataSelectors } from '@app/store/data/data.selectors';
import { SetUserImage } from '@app/store/data/data.classes';
import { UserInGroup } from '@app/models/groupEntitiesData.model';
import { UserService } from '@app/services/dai.service';
import { eqSet } from '@app/shared/common/utils';
import { chain, sortBy } from 'lodash';

@Component({
	selector: 'app-user-in-group-search',
	templateUrl: './user-in-group-search.component.html',
	styleUrls: ['./user-in-group-search.component.scss']
})
export class UserInGroupSearchComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() multiple: boolean = false;
	@Output() usersChange = new EventEmitter<User[]>();
	@Input() selectedId: string[] = [];
	@Select(DataSelectors.userGroupList) grouplist$!: Observable<UserInGroup[]>;
	@Select(DataSelectors.userList) userList$!: Observable<UserInGroup[]>;
	private onDestory$: Subject<void>;
	form: FormControl = new FormControl('');
	filtered$?: Observable<User[] | null>;
	startRender: boolean = false;
	userInGroupRender: any[] = [];
	_userList: User[] = [];
	_cachedGrp: Set<string> = new Set<string>([]);
	_groupSelected: string[] = [];
	private allowFetchPerSection: number = 5;
	get userList() {
		return this.userService.userList;
	}
	get displayUser() {
		if (this._groupSelected?.length > 0) {
			return this.userList.filter((x: any) => this._groupSelected.some(daicd => daicd === x.daicd));
		}
		return this.userList;
	}
	constructor(
		protected store: Store,
		protected cd: ChangeDetectorRef,
		protected userService: UserService
	) {
		this.onDestory$ = new Subject();
	}
	ngOnDestroy(): void {
		this.onDestory$.next();
	}

	avatarTrigger(i: number = 0) {
		if (i < 0 || i >= this.userList.length) return EMPTY;
		const { daicd } = this.userList[i];
		return this.store.dispatch(new SetUserImage(daicd)).pipe(takeUntil(this.onDestory$)).subscribe(() => {
			this.avatarTrigger(i + this.allowFetchPerSection);
		});
	}

	ngAfterViewInit(): void {
		// throw new Error('Method not implemented.');
		setTimeout(() => {
			this.cd.detectChanges();
			this.filtered$ = this.form.valueChanges.pipe(
				startWith(''),
				map(
					(value) => {
						this._userList = this.displayUser?.filter(
							({ name, kana }) => name.includes(value) || kana?.includes(value)
						) ?? [];
						return this._userList;
					}
				)
			);
			this.startRender = true;
			this.cd.detectChanges();
		});
		new Array(this.allowFetchPerSection).fill(0).map((_, i) => this.avatarTrigger(i));
		this.cd.detectChanges();
	}

	private _transformer = (node: any, level: number) => {
		return {
			expandable: !!node.grps && node.grps.length > 0,
			name: node.grpkbnnm || node.grpnm,
			level: level,
			grpunit: node.grpunit || undefined,
			grpcd: node.grpcd || undefined,
			last: node.last || false,
			lastParent: node.lastParent || false,
		};
	};

	treeControl = new FlatTreeControl<any>(
		node => node.level,
		node => node.expandable,
	);
	treeFlattener = new MatTreeFlattener(
		this._transformer,
		node => node.level,
		node => node.expandable,
		node => node.grps,
	);

	ngOnInit(): void {
		this.grouplist$.subscribe(() => {
			const useringroup = this.userService.groupList;
			const newList = useringroup.map(x => x.objcd);
			const newSet = new Set<string>(newList);
			if (useringroup && useringroup.length > 0 && !eqSet(newSet, this._cachedGrp)) {
				this.userInGroupRender = this.readDataGrp(useringroup);
				this._cachedGrp = newSet;
			}
		})

	}

	listControl(isEmpty: boolean, isFull: boolean) {
		if (isEmpty) {
			// only uncheck filtered data
			this.selectedId = this.selectedId.filter(daicd => !this._userList.some(x => x.daicd === daicd));
		}
		if (isFull) {
			this.selectedId = this._userList.map(x => x.daicd);
		}
		this.getUserChange();
	}

	onChange(user: User): void {
		if (!user.daicd) return;
		const dedupeSelectedId = Array.from(new Set(this.selectedId));
		if (!this.multiple) {
			this.selectedId = [user.daicd];
			this.getUserChange();
			return;
		}
		if (this.selectedId.length === 0) {
			this.selectedId = [user.daicd];
		} else {
			let index = dedupeSelectedId.findIndex(x => x === user.daicd);
			if (index >= 0) {
				dedupeSelectedId.splice(index, 1);
			}
			else {
				dedupeSelectedId.push(user.daicd);
			}
			this.selectedId = dedupeSelectedId;
		}
		this.getUserChange();
	}

	getUserChange(): void {
		let users = this.userList.filter(user => this.selectedId.some(x => x === user.daicd));
		this.usersChange.emit(users);
	}

	checkSelect(daicd: string): boolean {
		return this.selectedId.some(x => x === daicd);
	}

	generateGrpUnit(element: any) {
		if (!element) return undefined;
		return element.corpcd
			+ element.officecd
			+ element.blockcd
			+ element.subcd
			+ element.unitcd
			+ element.grpkbncd
			+ element.grpcd;
	}

	readDataGrp(grpData: any[] = []) {
		grpData = sortBy(grpData, ['grpkbnnm', 'grp.grpcd'], ['asc', 'asc']);
		const _grpData = chain(grpData)
			.groupBy((grp: any) => { return grp.corpcd + grp.officecd + grp.blockcd + grp.subcd + grp.unitcd + grp.grpkbncd; })
			.map((grpGroup: any) => {
				const group_grp = chain(grpGroup)
					.groupBy((groupByGrp: any) => { return this.generateGrpUnit(groupByGrp); })
					.map((item: any) => {
						const itms = item.map((x: any) => { return x.objcd; });
						return {
							grpunit: this.generateGrpUnit(item[0]),
							grpnm: item[0].grpnm,
							grpcd: item[0].grpcd,
							daiCdList: itms
						};
					})
					.value();
				return {
					grpkbncd: grpGroup[0].grpkbncd,
					grpkbnnm: grpGroup[0].grpkbnnm,
					grps: group_grp
				};
			})
			.value();
		if (_grpData && _grpData.length > 0) {
			const result = _grpData.map((item: any, index, arr) => {
				if (index === arr.length - 1) {
					item.last = true;
				}
				if (item.grps && item.grps.length > 0) {
					item.grps = item.grps.map((child: any, _index: number, _arr: any) => {
						return { ...child, last: _index === _arr.length - 1 ? true : false, lastParent: item.last ? true : false };
					});
				}
				return item;
			});
			return result;
		}
		return [];
	}

	filterByGroup(node: any) {
		if (!node || !node.grpcd || node.grpcd === '') {
			this._groupSelected = [];
		} else {
			let { grpunit } = node;
			this._groupSelected = this.userService.groupList.filter((x: any) => this.generateGrpUnit(x) === grpunit).map((x: any) => x.objcd);
		}
		this.form.setValue(this.form.value);
	}
}

